import { X } from 'lucide-react';
import {
  Controller,
  useForm,
  useWatch,
  type Control,
  type SubmitErrorHandler,
  type SubmitHandler,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Card,
  Dialog,
  FieldError,
  Flex,
  Form,
  HeaderItalic,
  MenuLinkButton,
  PillToggle,
  Select,
  Text,
  TextArea,
  TextField,
} from '@/components/common';
import {
  V90_ETHNICITY_OPTIONS,
  V90_GENDER_OPTIONS,
  V90_INTEREST_OPTIONS,
  V90_INTIMACY_OPTIONS,
  V90_LANGUAGE_OPTIONS,
  V90_OPEN_TO_OPTIONS,
  V90_ORIENTATION_OPTIONS,
  V90_PRONOUN_OPTIONS,
  V90_RELATIONSHIP_STATUS_OPTIONS,
  V90_RELATIONSHIP_TYPE_OPTIONS,
} from '@/constants';
import { analyticsClient } from '@/lib';
import { formatBirthdayDisplay } from '@/lib/birthday';
import { ProfileSchemaKey, useProfileSchema, type ProfileFormValues } from '@/lib/validators';
import { addProfilePhoto, deleteProfilePhoto } from '@/services/storageService';
import { userService } from '@/services/userService';
import { type ProfilePhoto, type UserProfile } from '@/types';
import { normalizePhotos, removedPhotos, reorderPhotos } from '@/utils';
import { CityField } from './CityField';
import { HeightField } from './HeightField';
import { PendingChangesDialog } from './PendingChangesDialog';
import { PhotoGrid } from './PhotoGrid';

/**
 * Edit profile form (v90-aligned sections).
 */

const MAX_PHOTOS = 3;
const MAX_INTRO = 300;
const MAX_IDENTITY = 3;
const INTRO_FORM_ID = 'edit-profile-form';

const EDIT_PROFILE_SCHEMA_KEYS = [
  ProfileSchemaKey.NAME,
  ProfileSchemaKey.BIO,
  ProfileSchemaKey.HEIGHT,
  ProfileSchemaKey.PRONOUNS,
  ProfileSchemaKey.CITY,
  ProfileSchemaKey.GENDERS,
  ProfileSchemaKey.ORIENTATIONS,
  ProfileSchemaKey.LANGUAGES,
  ProfileSchemaKey.ETHNICITIES,
  ProfileSchemaKey.INTERESTS,
  ProfileSchemaKey.OPEN_TO,
  ProfileSchemaKey.RELATIONSHIP_TYPES,
  ProfileSchemaKey.RELATIONSHIP_STATUS,
  ProfileSchemaKey.KINKS,
  ProfileSchemaKey.PHOTOS,
] as const;

type EditProfileFormValues = Omit<ProfileFormValues, 'birthday'>;

type SheetId =
  | 'openTo'
  | 'gender'
  | 'orientation'
  | 'status'
  | 'relType'
  | 'interests'
  | 'kinks'
  | 'languages'
  | 'ethnicity'
  | null;

export interface EditProfileHandle {
  handleBack: () => void;
}

interface EditProfileProps {
  profile: UserProfile;
  onUpdate: (updated: UserProfile) => void;
  /** Lets the page header Save reflect in-flight submit. */
  onSavingChange?: (saving: boolean) => void;
  onBack: () => void;
}

interface DialogConfig {
  name: keyof EditProfileFormValues;
  title: string;
  options: string[];
  multiple: boolean;
  isIdentity: boolean;
}

function asList(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value.trim() ? [value] : [];
}

function summarize(values: string[], empty: string): string {
  const cleaned = values.map(v => v.trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned.join(', ') : empty;
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <Flex direction="column" gap="4">
        <HeaderItalic color="red" size="4">
          {title}
        </HeaderItalic>
        {children}
      </Flex>
    </Card>
  );
}

function FieldGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Flex direction="column" gap="2">
      <Text size="2" color="gray">
        {label}
      </Text>
      {children}
    </Flex>
  );
}

function MultiSelectDialog({
  open,
  onOpenChange,
  dialog,
  control,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dialog: DialogConfig | null;
  control: Control<EditProfileFormValues>;
}) {
  const { t } = useTranslation('common');
  if (!open || !dialog) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="2" maxWidth="400px" aria-describedby={undefined}>
        <Dialog.Title className="sr-only">{dialog.title}</Dialog.Title>
        <Flex direction="column" gap="4">
          <Flex justify="between" align="center" gap="3">
            <Box flexGrow="1" flexShrink="1" minWidth="0" style={{ overflow: 'hidden' }}>
              <HeaderItalic size="4">{dialog.title}</HeaderItalic>
            </Box>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              <X size={24} />
            </Button>
          </Flex>
          <Box style={{ maxHeight: '20rem', overflowY: 'auto' }}>
            <Controller
              name={dialog.name}
              control={control}
              render={({ field }) =>
                dialog.multiple ? (
                  <PillToggle
                    type="multiple"
                    options={dialog.options}
                    value={field.value as string[]}
                    onValueChange={value => {
                      (field.onChange as (value: string[]) => void)(value);
                      field.onBlur();
                    }}
                    disabledOptions={
                      dialog.isIdentity
                        ? dialog.options.filter(
                            o =>
                              !(field.value as string[]).includes(o) &&
                              (field.value as string[]).length >= MAX_IDENTITY
                          )
                        : undefined
                    }
                  />
                ) : (
                  <PillToggle
                    type="single"
                    options={dialog.options}
                    value={field.value as string}
                    onValueChange={value => {
                      (field.onChange as (value: string) => void)(value);
                      field.onBlur();
                    }}
                  />
                )
              }
            />
          </Box>
          <Button
            type="button"
            color="gray"
            highContrast
            size="lg"
            fullWidth
            onClick={() => onOpenChange(false)}
          >
            {t('actions.apply')}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

const EditProfile = forwardRef<EditProfileHandle, EditProfileProps>(function EditProfile(
  { profile, onUpdate, onSavingChange, onBack },
  ref
) {
  const { t } = useTranslation('profile');
  const empty = t('edit.emptyValue');

  const [dialog, setDialog] = useState<DialogConfig | null>(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Photos uploaded to Storage during this session but not yet saved to the
  // profile — deleted again if the edit is abandoned.
  const stagedUploadsRef = useRef<Map<string, ProfilePhoto>>(new Map());

  const schema = useProfileSchema(EDIT_PROFILE_SCHEMA_KEYS);

  const defaultValues = useMemo<EditProfileFormValues>(
    () => ({
      name: profile.name || '',
      bio: profile.bio || '',
      height: profile.height != null && profile.height > 0 ? profile.height : undefined,
      pronouns: profile.pronouns || '',
      city: profile.city || '',
      selectedGenders: asList(profile.gender),
      selectedOrientations: asList(profile.orientation),
      selectedLanguages: asList(profile.languages),
      selectedEthnicities: asList(profile.ethnicity),
      selectedInterests: asList(profile.interests),
      selectedOpenTo: asList(profile.openTo),
      selectedRelationshipTypes: asList(profile.relationshipTypes),
      relationshipStatus: profile.relationshipStatus || '',
      selectedKinks: asList(profile.intimacy),
      photos: normalizePhotos(profile.photos),
    }),
    [profile]
  );

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<EditProfileFormValues>({
    resolver: zodResolver(schema),
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
    mode: 'onBlur',
  });

  const formValues = useWatch({ control }) as EditProfileFormValues;

  useImperativeHandle(
    ref,
    () => ({
      handleBack: () => {
        if (isDirty) {
          setLeaveDialogOpen(true);
        } else {
          onBack();
        }
      },
    }),
    [isDirty, onBack]
  );

  useEffect(() => {
    onSavingChange?.(isSubmitting);
  }, [isSubmitting, onSavingChange]);

  // Delete staged uploads if the component unmounts without saving (e.g.
  // navigating to a subpage, which bypasses the pending-changes dialog).
  useEffect(
    () => () => {
      stagedUploadsRef.current.forEach(photo => {
        deleteProfilePhoto(photo).catch(err =>
          console.warn('Failed to delete unsaved photo:', err)
        );
      });
    },
    []
  );

  const getDialogConfig = (id: Exclude<SheetId, null>): DialogConfig => {
    switch (id) {
      case 'openTo':
        return {
          name: 'selectedOpenTo',
          title: t('edit.fields.openTo'),
          options: V90_OPEN_TO_OPTIONS,
          multiple: true,
          isIdentity: false,
        };
      case 'gender':
        return {
          name: 'selectedGenders',
          title: t('edit.fields.genders'),
          options: V90_GENDER_OPTIONS,
          multiple: true,
          isIdentity: true,
        };
      case 'orientation':
        return {
          name: 'selectedOrientations',
          title: t('edit.fields.orientation'),
          options: V90_ORIENTATION_OPTIONS,
          multiple: true,
          isIdentity: true,
        };
      case 'status':
        return {
          name: 'relationshipStatus',
          title: t('edit.fields.status'),
          options: V90_RELATIONSHIP_STATUS_OPTIONS,
          multiple: false,
          isIdentity: false,
        };
      case 'relType':
        return {
          name: 'selectedRelationshipTypes',
          title: t('edit.fields.relationshipType'),
          options: V90_RELATIONSHIP_TYPE_OPTIONS,
          multiple: true,
          isIdentity: false,
        };
      case 'interests':
        return {
          name: 'selectedInterests',
          title: t('edit.fields.interests'),
          options: V90_INTEREST_OPTIONS,
          multiple: true,
          isIdentity: false,
        };
      case 'kinks':
        return {
          name: 'selectedKinks',
          title: t('edit.fields.kinks'),
          options: V90_INTIMACY_OPTIONS,
          multiple: true,
          isIdentity: false,
        };
      case 'languages':
        return {
          name: 'selectedLanguages',
          title: t('edit.fields.languages'),
          options: V90_LANGUAGE_OPTIONS,
          multiple: true,
          isIdentity: false,
        };
      case 'ethnicity':
        return {
          name: 'selectedEthnicities',
          title: t('edit.fields.ethnicity'),
          options: V90_ETHNICITY_OPTIONS,
          multiple: true,
          isIdentity: false,
        };
    }
  };

  const openDialog = (id: Exclude<SheetId, null>) => setDialog(getDialogConfig(id));

  const uploadPhoto = async (blob: Blob, filename: string) => {
    setUploading(true);
    setError(null);
    try {
      const current = getValues('photos');
      const updated = await addProfilePhoto(profile.uid, current, blob, filename);
      const currentIds = new Set(current.map(photo => photo.id));
      updated
        .filter(photo => !currentIds.has(photo.id))
        .forEach(photo => stagedUploadsRef.current.set(photo.id, photo));
      setValue('photos', updated, { shouldDirty: true, shouldValidate: true });
    } catch (err) {
      console.error(err);
      const errorMessage = err instanceof Error ? err.message : t('edit.errors.readImageFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Photo removal is staged in the form; the Storage object is only deleted
  // once the change is saved. A staged upload can be deleted right away since
  // no profile references it.
  const removePhoto = (indexToDrop: number) => {
    setError(null);
    const current = getValues('photos');
    const removed = current[indexToDrop];
    setValue(
      'photos',
      current
        .filter((_, idx) => idx !== indexToDrop)
        .map((photo, position) => ({ ...photo, position })),
      { shouldDirty: true, shouldValidate: true }
    );
    if (removed && stagedUploadsRef.current.has(removed.id)) {
      stagedUploadsRef.current.delete(removed.id);
      deleteProfilePhoto(removed).catch(err =>
        console.warn('Failed to delete unsaved photo:', err)
      );
    }
  };

  const handlePhotoReorder = (from: number, to: number) => {
    setError(null);
    setValue('photos', reorderPhotos(getValues('photos'), from, to), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const saveProfile = async (data: EditProfileFormValues): Promise<UserProfile | null> => {
    setError(null);
    try {
      const updates: Partial<UserProfile> = {
        name: data.name.trim(),
        bio: data.bio.trim(),
        pronouns: data.pronouns.trim() || undefined,
        city: data.city.trim() || undefined,
        gender: data.selectedGenders,
        orientation: data.selectedOrientations,
        languages: data.selectedLanguages,
        ethnicity: data.selectedEthnicities,
        interests: data.selectedInterests,
        openTo: data.selectedOpenTo,
        relationshipTypes: data.selectedRelationshipTypes,
        relationshipStatus: data.relationshipStatus.trim() || undefined,
        // Spots are saved on EditSpotsPage; keep latest from profile subscription.
        spots: asList(profile.spots),
        intimacy: data.selectedKinks,
        photos: data.photos,
        height: data.height,
      };

      await userService.updateProfile(profile.uid, updates);

      // Persisted photos dropped from the form are unreferenced now — delete
      // their Storage objects. Staged uploads that made it into the save are
      // part of the profile and no longer pending cleanup.
      data.photos.forEach(photo => stagedUploadsRef.current.delete(photo.id));
      removedPhotos(profile.photos, data.photos).forEach(photo => {
        deleteProfilePhoto(photo).catch(err =>
          console.warn('Failed to delete removed photo:', err)
        );
      });

      const updatedProfile: UserProfile = {
        ...profile,
        ...updates,
      };
      toast.success(t('edit.saveSuccess'));
      analyticsClient.capture('profile_updated');
      return updatedProfile;
    } catch (err: unknown) {
      console.error(err);
      const errorMessage = err instanceof Error ? err.message : t('edit.errors.saveFailed');
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    }
  };

  const onValid: SubmitHandler<EditProfileFormValues> = async data => {
    const updated = await saveProfile(data);
    if (updated) onUpdate(updated);
  };

  const onSaveAndExit: SubmitHandler<EditProfileFormValues> = async data => {
    const updated = await saveProfile(data);
    if (updated) {
      onUpdate(updated);
      onBack();
    }
  };

  const onInvalid: SubmitErrorHandler<EditProfileFormValues> = formErrors => {
    const first = Object.values(formErrors)[0]?.message as string | undefined;
    if (first) {
      setError(first);
      toast.error(first);
    }
  };

  return (
    <>
      <Flex asChild direction="column" gap="7" pb="8">
        <Form.Root
          id={INTRO_FORM_ID}
          onSubmit={e => void handleSubmit(onValid, onInvalid)(e)}
          noValidate
          aria-busy={isSubmitting}
        >
          {error && (
            <Text size="2" color="red">
              {error}
            </Text>
          )}

          <SectionCard title={t('edit.sections.personalInfo')}>
            <FieldGroup label={t('edit.fields.name')}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField.Root
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    id="edit-name"
                    autoComplete="nickname"
                    aria-invalid={fieldState.error ? true : undefined}
                  />
                )}
              />
              <FieldError message={errors.name?.message} />
            </FieldGroup>
            {profile.birthday && (
              <FieldGroup label={t('edit.fields.birthDate')}>
                <Text size="2">{formatBirthdayDisplay(profile.birthday)}</Text>
                <Text size="1" color="gray">
                  {t('edit.fields.birthDateLocked')}
                </Text>
              </FieldGroup>
            )}
          </SectionCard>

          <SectionCard title={t('edit.sections.photos')}>
            <PhotoGrid
              photos={formValues.photos}
              uploading={uploading}
              onUpload={uploadPhoto}
              onRemove={removePhoto}
              onReorder={handlePhotoReorder}
              maxPhotos={MAX_PHOTOS}
            />
            <FieldError message={errors.photos?.message} />
          </SectionCard>

          <SectionCard title={t('edit.sections.intro')}>
            <Controller
              name="bio"
              control={control}
              render={({ field, fieldState }) => (
                <TextArea
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  rows={4}
                  placeholder={t('edit.introPlaceholder')}
                  id="edit-bio"
                  resize="none"
                  maxLength={MAX_INTRO}
                  aria-invalid={fieldState.error ? true : undefined}
                />
              )}
            />
            <FieldError message={errors.bio?.message} />
            <Text size="1" color="gray" align="right">
              {t('edit.introCount', { count: formValues.bio.length, max: MAX_INTRO })}
            </Text>
          </SectionCard>

          <SectionCard title={t('edit.sections.openTo')}>
            <MenuLinkButton
              weight="regular"
              title={summarize(formValues.selectedOpenTo, empty)}
              onClick={() => openDialog('openTo')}
            />
            <FieldError message={errors.selectedOpenTo?.message} />
          </SectionCard>

          <SectionCard title={t('edit.sections.identity')}>
            <FieldGroup label={t('edit.fields.genders')}>
              <MenuLinkButton
                weight="regular"
                title={summarize(formValues.selectedGenders, empty)}
                onClick={() => openDialog('gender')}
              />
              <FieldError message={errors.selectedGenders?.message} />
            </FieldGroup>
            <FieldGroup label={t('edit.fields.pronouns')}>
              <Controller
                name="pronouns"
                control={control}
                render={({ field }) => (
                  <Select.Root
                    value={field.value}
                    onValueChange={field.onChange}
                    onOpenChange={open => {
                      if (!open) field.onBlur();
                    }}
                  >
                    <Select.Trigger
                      variant="surface"
                      placeholder={t('common:form.select') as string}
                    />
                    <Select.Content>
                      <Select.Group>
                        {V90_PRONOUN_OPTIONS.map(option => (
                          <Select.Item key={option} value={option}>
                            {option}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                )}
              />
            </FieldGroup>
            <FieldGroup label={t('edit.fields.orientation')}>
              <MenuLinkButton
                weight="regular"
                title={summarize(formValues.selectedOrientations, empty)}
                onClick={() => openDialog('orientation')}
              />
            </FieldGroup>
          </SectionCard>

          <SectionCard title={t('edit.sections.whereImAt')}>
            <FieldGroup label={t('edit.fields.status')}>
              <MenuLinkButton
                weight="regular"
                title={formValues.relationshipStatus.trim() || empty}
                onClick={() => openDialog('status')}
              />
            </FieldGroup>
            <FieldGroup label={t('edit.fields.relationshipType')}>
              <MenuLinkButton
                weight="regular"
                title={summarize(formValues.selectedRelationshipTypes, empty)}
                onClick={() => openDialog('relType')}
              />
            </FieldGroup>
          </SectionCard>

          <SectionCard title={t('edit.sections.myWorld')}>
            <FieldGroup label={t('edit.fields.interests')}>
              <MenuLinkButton
                weight="regular"
                title={summarize(formValues.selectedInterests, empty)}
                onClick={() => openDialog('interests')}
              />
              <FieldError message={errors.selectedInterests?.message} />
            </FieldGroup>

            <FieldGroup label={t('edit.fields.spots')}>
              <MenuLinkButton
                weight="regular"
                title={summarize(asList(profile.spots), empty)}
                to="/account/my-profile/edit/spots"
              />
            </FieldGroup>
            <FieldGroup label={t('edit.fields.kinks')}>
              <MenuLinkButton
                weight="regular"
                title={summarize(formValues.selectedKinks, empty)}
                onClick={() => openDialog('kinks')}
              />
            </FieldGroup>
            <FieldGroup label={t('edit.fields.languages')}>
              <MenuLinkButton
                weight="regular"
                title={summarize(formValues.selectedLanguages, empty)}
                onClick={() => openDialog('languages')}
              />
              <FieldError message={errors.selectedLanguages?.message} />
            </FieldGroup>
            <FieldGroup label={t('edit.fields.ethnicity')}>
              <MenuLinkButton
                weight="regular"
                title={summarize(formValues.selectedEthnicities, empty)}
                onClick={() => openDialog('ethnicity')}
              />
            </FieldGroup>
          </SectionCard>

          <SectionCard title={t('edit.sections.basics')}>
            <FieldGroup label={t('edit.fields.livesIn')}>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <CityField value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                )}
              />
            </FieldGroup>
            <FieldGroup label={t('edit.fields.height')}>
              <Controller
                name="height"
                control={control}
                render={({ field, fieldState }) => (
                  <HeightField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    data-testid="edit-height"
                    error={fieldState.error?.message}
                  />
                )}
              />
              <FieldError message={errors.height?.message} />
            </FieldGroup>
          </SectionCard>
        </Form.Root>
      </Flex>

      {dialog && (
        <MultiSelectDialog
          open
          onOpenChange={open => !open && setDialog(null)}
          dialog={dialog}
          control={control}
        />
      )}

      <PendingChangesDialog
        open={leaveDialogOpen}
        onOpenChange={setLeaveDialogOpen}
        onKeepEditing={() => setLeaveDialogOpen(false)}
        onDiscard={() => {
          setLeaveDialogOpen(false);
          stagedUploadsRef.current.forEach(url => {
            deleteProfilePhoto(url).catch(err =>
              console.warn('Failed to delete unsaved photo:', err)
            );
          });
          stagedUploadsRef.current.clear();
          reset(defaultValues);
          onBack();
        }}
        onSaveAndExit={() => {
          setLeaveDialogOpen(false);
          handleSubmit(onSaveAndExit, onInvalid)();
        }}
      />
    </>
  );
});

export { INTRO_FORM_ID as EDIT_PROFILE_FORM_ID };
export default EditProfile;
